import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FaChevronDown, FaTimes, FaArrowLeft } from "react-icons/fa";
import "../assets/css/EditTenant.css";
import "../assets/css/ViewCustomer.css";
import "../assets/css/Projects.css";
import "../assets/css/DigixLeads.css";
import "../assets/css/Issues.css";
import axiosInstance from "../components/api/axiosInstance";
import ProjectFileAttachments from "./ProjectFileAttachments";
import { extractCreatedProjectId, uploadProjectFile } from "./projectFileService";
import { canPerformActionOnPage, PAGES, ACTIONS } from "../utils/roleAccess";
import { currencyRowsFromResponseData, normalizeCurrencyRow } from "../utils/currencyList";
import { getAuthItem } from "../utils/authStorage";
import {
  getCustomerSelectLabel,
  getCustomerSelectSearchText,
  mapCustomerListItemFromApi
} from "../utils/formatters";

const VISIBILITY_OPTIONS = ["Private", "Public", "Team"];

const INITIAL_SUBPROJECT_FORM = {
  subProjectName: "",
  shortCode: "",
  description: "",
  domain: "",
  category: "",
  startDate: "",
  endDate: "",
  actualStartDate: "",
  actualEndDate: "",
  visibility: "Private",
  statusId: "2",
  userIds: []
};

function AddProject({ onBack, onAddProject }) {
  const navigate = useNavigate();
  const role = useMemo(() => {
    try {
      const u = getAuthItem("user");
      if (!u) return null;
      const user = JSON.parse(u);
      const primaryRole = Array.isArray(user.roles) && user.roles.length > 0 ? user.roles[0] : null;
      return (
        (primaryRole && (primaryRole.role_name || primaryRole.name || primaryRole.role)) ||
        user.role_name ||
        user.role ||
        null
      );
    } catch {
      return null;
    }
  }, []);
  const canAddProjectFiles = canPerformActionOnPage(PAGES.PROJECTS, ACTIONS.ADD, role);
  const [createdProject, setCreatedProject] = useState(null);
  const [subprojects, setSubprojects] = useState([]);
  const [subprojectForm, setSubprojectForm] = useState(INITIAL_SUBPROJECT_FORM);
  const [subprojectSubmitting, setSubprojectSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    projectName: "",
    shortCode: "",
    description: "",
    notes: "",
    domain: "",
    category: "",
    startDate: "",
    endDate: "",
    actualStartDate: "",
    actualEndDate: "",
    totalCost: "",
    currencyId: "",
    prioritySeq: "",
    visibility: "Private",
    statusId: "2",
    clientId: "",
    userIds: []
  });
  const [customers, setCustomers] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [domains, setDomains] = useState([]);
  const [loadingDomains, setLoadingDomains] = useState(true);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [showTeamDropdown, setShowTeamDropdown] = useState(false);
  const [showSubTeamDropdown, setShowSubTeamDropdown] = useState(false);
  const [teamMemberSearchQuery, setTeamMemberSearchQuery] = useState("");
  const [subTeamMemberSearchQuery, setSubTeamMemberSearchQuery] = useState("");
  const [domainDropdownOpen, setDomainDropdownOpen] = useState(false);
  const [domainSearchQuery, setDomainSearchQuery] = useState("");
  const domainDropdownRef = useRef(null);
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const [customerSearchQuery, setCustomerSearchQuery] = useState("");
  const customerDropdownRef = useRef(null);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [categorySearchQuery, setCategorySearchQuery] = useState("");
  const categoryDropdownRef = useRef(null);
  const [subDomainDropdownOpen, setSubDomainDropdownOpen] = useState(false);
  const [subDomainSearchQuery, setSubDomainSearchQuery] = useState("");
  const subDomainDropdownRef = useRef(null);
  const [subCategoryDropdownOpen, setSubCategoryDropdownOpen] = useState(false);
  const [subCategorySearchQuery, setSubCategorySearchQuery] = useState("");
  const subCategoryDropdownRef = useRef(null);
  const teamDropdownRef = useRef(null);
  const subTeamDropdownRef = useRef(null);
  const [statuses, setStatuses] = useState([]);
  const [loadingStatuses, setLoadingStatuses] = useState(true);
  const [currencies, setCurrencies] = useState([]);
  const [loadingCurrencies, setLoadingCurrencies] = useState(true);
  const [currencyFetchError, setCurrencyFetchError] = useState(null);

  // Project subscription (FICO)
  const [subscriptionForm, setSubscriptionForm] = useState({
    createOnProjectCreate: true,
    planId: "1",
    startDate: "",
    endDate: "",
    amount: "",
    status: "Active"
  });
  const [subscriptionResult, setSubscriptionResult] = useState(null);
  const [subscriptionError, setSubscriptionError] = useState("");

  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);

  useEffect(() => {
    const fetchPlans = async () => {
      setLoadingPlans(true);
      const fallback = [{ id: 1, name: "Monthly" }];
      try {
        const res = await axiosInstance.get("/fico/subscription-plans");
        const arr = Array.isArray(res?.data) ? res.data : res?.data?.data ?? [];

        const mapped = (arr || [])
          .map((p) => {
            const id = Number(p?.Plan_Id ?? p?.plan_id ?? p?.id);
            const name = String(p?.Plan_Name ?? p?.plan_name ?? p?.name ?? "").trim();
            if (!id || Number.isNaN(id)) return null;
            return { id, name: name || `Plan ${id}` };
          })
          .filter(Boolean);

        const finalPlans = mapped.length > 0 ? mapped : fallback;
        setPlans(finalPlans);

        setSubscriptionForm((prev) => {
          const stillValid = finalPlans.some((pl) => String(pl.id) === String(prev.planId));
          if (stillValid) return prev;
          return { ...prev, planId: String(finalPlans[0]?.id ?? 1) };
        });
      } catch (err) {
        console.error("Error fetching subscription plans:", err);
        setPlans(fallback);
        setSubscriptionForm((prev) => (prev.planId ? prev : { ...prev, planId: "1" }));
      } finally {
        setLoadingPlans(false);
      }
    };

    fetchPlans();
  }, []);

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const response = await axiosInstance.get("/clients");
        const arr = Array.isArray(response.data) ? response.data : response.data?.data || [];
        setCustomers(arr.map(mapCustomerListItemFromApi).filter(Boolean));
      } catch (err) {
        console.error("Error fetching customers:", err);
      } finally {
        setLoadingCustomers(false);
      }
    };
    const fetchUsers = async () => {
      try {
        const response = await axiosInstance.get("/users");
        const arr = Array.isArray(response.data) ? response.data : response.data?.data || response.data?.users || [];
        setUsers(arr.map((u) => {
          const id = u.user_id || u.id || u.User_Id;
          const name = `${u.first_name || u.firstName || ""} ${u.last_name || u.lastName || ""}`.trim() || u.email || "—";
          return { id, name };
        }).filter((x) => x.id));
      } catch (err) {
        console.error("Error fetching users:", err);
      } finally {
        setLoadingUsers(false);
      }
    };
    const fetchDomains = async () => {
      try {
        const response = await axiosInstance.get("/domains");
        const arr = Array.isArray(response.data) ? response.data : response.data?.data || [];
        setDomains(
          arr
            .map((d) => {
              const id = d.Domain_Id || d.domain_id || d.id;
              const name = d.Domain_Name || d.domain_name || d.name || "—";
              return { id, name };
            })
            .filter((x) => x.id)
        );
      } catch (err) {
        console.error("Error fetching domains:", err);
        setDomains([]);
      } finally {
        setLoadingDomains(false);
      }
    };
    const fetchCategories = async () => {
      try {
        const response = await axiosInstance.get("/project-categories");
        const arr = Array.isArray(response.data) ? response.data : response.data?.data || [];
        setCategories(
          arr
            .map((c) => {
              const id = c.Category_Id || c.category_id || c.id;
              const name = c.Category_Name || c.category_name || c.name || "—";
              return { id, name };
            })
            .filter((x) => x.id)
        );
      } catch (err) {
        console.error("Error fetching project categories:", err);
        setCategories([]);
      } finally {
        setLoadingCategories(false);
      }
    };
    const fetchStatuses = async () => {
      setLoadingStatuses(true);
      try {
        const response = await axiosInstance.get("/project-statuses");
        const arr = Array.isArray(response.data) ? response.data : response.data?.data || [];
        const mapped = arr
          .map((s) => ({
            id: s.Status_Id ?? s.status_id ?? s.id,
            name: s.Status ?? s.status ?? s.name ?? "—"
          }))
          .filter((x) => x.id)
          .sort((a, b) => a.id - b.id);
        setStatuses(mapped);
      } catch (err) {
        console.error("Error fetching project statuses:", err);
      } finally {
        setLoadingStatuses(false);
      }
    };
    const fetchCurrencies = async () => {
      setLoadingCurrencies(true);
      setCurrencyFetchError(null);
      const fallbackList = [
        { id: 1, name: "Indian Rupee", code: "INR", symbol: "₹" },
        { id: 2, name: "US Dollar", code: "USD", symbol: "$" }
      ];
      try {
        const response = await axiosInstance.get("/currencies");
        const rows = currencyRowsFromResponseData(response.data);
        const mapped = rows.map(normalizeCurrencyRow).filter(Boolean);
        const filtered = mapped.filter((c) => {
          const code = String(c?.code ?? "").trim().toUpperCase();
          const symbol = String(c?.symbol ?? "").trim();
          const isInr = code === "INR" || symbol === "₹";
          // USD often uses "$" as symbol, but many currencies share "$". Prefer code === "USD".
          const isUsd = code === "USD" || (code === "" && symbol === "$");
          return isInr || isUsd;
        });

        setCurrencies(filtered.length > 0 ? filtered : fallbackList);
        if (mapped.length === 0 && rows.length > 0) {
          setCurrencyFetchError("Currencies were returned in an unexpected format; showing INR and USD as fallback. Check API field names or network response.");
        } else if (mapped.length > 0 && filtered.length === 0) {
          setCurrencyFetchError("Only INR (₹) and USD ($) are supported; showing INR and USD as fallback.");
        }
      } catch (err) {
        console.error("Error fetching currencies:", err);
        const msg =
          err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          "Could not load currencies.";
        setCurrencyFetchError(String(msg));
        setCurrencies(fallbackList);
      } finally {
        setLoadingCurrencies(false);
      }
    };
    fetchCustomers();
    fetchUsers();
    fetchDomains();
    fetchCategories();
    fetchStatuses();
    fetchCurrencies();
  }, []);

  // Set default status to "New" once statuses are loaded.
  // We only override when the user hasn't changed away from the initial default.
  useEffect(() => {
    if (!Array.isArray(statuses) || statuses.length === 0) return;

    const newStatus = statuses.find((s) => String(s?.name ?? "")
      .trim()
      .toLowerCase() === "new");
    if (!newStatus) return;

    const newStatusId = String(newStatus.id);
    if (!newStatusId) return;

    setFormData((prev) => {
      const current = String(prev?.statusId ?? "");
      if (!current || current === "2") {
        return { ...prev, statusId: newStatusId };
      }
      return prev;
    });
  }, [statuses]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (domainDropdownRef.current && !domainDropdownRef.current.contains(e.target)) { setDomainDropdownOpen(false); setDomainSearchQuery(""); }
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(e.target)) { setCustomerDropdownOpen(false); setCustomerSearchQuery(""); }
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(e.target)) { setCategoryDropdownOpen(false); setCategorySearchQuery(""); }
      if (subDomainDropdownRef.current && !subDomainDropdownRef.current.contains(e.target)) { setSubDomainDropdownOpen(false); setSubDomainSearchQuery(""); }
      if (subCategoryDropdownRef.current && !subCategoryDropdownRef.current.contains(e.target)) { setSubCategoryDropdownOpen(false); setSubCategorySearchQuery(""); }
      if (teamDropdownRef.current && !teamDropdownRef.current.contains(e.target)) { setShowTeamDropdown(false); setTeamMemberSearchQuery(""); }
      if (subTeamDropdownRef.current && !subTeamDropdownRef.current.contains(e.target)) { setShowSubTeamDropdown(false); setSubTeamMemberSearchQuery(""); }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleUserToggle = (userId) => {
    setFormData((prev) => {
      const ids = prev.userIds.includes(userId) ? prev.userIds.filter((id) => id !== userId) : [...prev.userIds, userId];
      return { ...prev, userIds: ids };
    });
  };

  const handleSubprojectInputChange = (e) => {
    const { name, value } = e.target;
    setSubprojectForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubprojectUserToggle = (userId) => {
    setSubprojectForm((prev) => {
      const ids = prev.userIds.includes(userId) ? prev.userIds.filter((id) => id !== userId) : [...prev.userIds, userId];
      return { ...prev, userIds: ids };
    });
  };

  const handleSubscriptionChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSubscriptionForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const today = new Date().toISOString().split("T")[0];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.projectName || !formData.shortCode || !formData.clientId) {
      alert("Please fill in required fields (Project Name, Short Code, Tenant)");
      return;
    }
    if (!formData.currencyId) {
      alert("Please select a currency.");
      return;
    }
    const totalCostNum = Number(String(formData.totalCost || "0").replace(/,/g, ""));
    if (Number.isNaN(totalCostNum) || totalCostNum < 0) {
      alert("Please enter a valid total cost (0 or greater).");
      return;
    }
    if (formData.startDate && formData.startDate < today) {
      alert("Start date cannot be in the past.");
      return;
    }
    if (formData.endDate && formData.endDate < today) {
      alert("End date cannot be in the past.");
      return;
    }
    if (formData.startDate && formData.endDate && formData.endDate < formData.startDate) {
      alert("End date cannot be before start date.");
      return;
    }
    if (formData.actualStartDate && formData.actualEndDate && formData.actualEndDate < formData.actualStartDate) {
      alert("Actual end date cannot be before actual start date.");
      return;
    }

    setSubscriptionResult(null);
    setSubscriptionError("");

    // Determine user performing the action
    let createdBy = 1;
    try {
      const userStr = getAuthItem("user");
      if (userStr) {
        const user = JSON.parse(userStr);
        createdBy = user.user_id || user.id || 1;
      }
    } catch (err) {
      console.error("Error reading user:", err);
    }
    const payload = {
      client_id: Number(formData.clientId),
      Project_Name: formData.projectName.trim(),
      Short_Code: formData.shortCode.trim(),
      Description: formData.description?.trim() || null,
      Total_Cost: totalCostNum,
      Currency_Id: Number(formData.currencyId),
      Domain_Id: formData.domain ? Number(formData.domain) : null,
      Category_Id: formData.category ? Number(formData.category) : null,
      Start_Date: formData.startDate || null,
      End_Date: formData.endDate || null,
      Actual_Start_Date: formData.actualStartDate || null,
      Actual_End_Date: formData.actualEndDate || null,
      Priority_Seq: formData.prioritySeq?.trim() || null,
      Created_By: createdBy,
      User_Ids: (formData.userIds || []).map((id) => Number(id)),
      Status_Id: formData.statusId ? parseInt(formData.statusId, 10) : 2
    };
    const notesTrim = formData.notes?.trim();
    if (notesTrim) payload.Notes = notesTrim;
    if (formData.visibility) payload.Visibility = formData.visibility;
    setSubmitting(true);
    try {
      const response = await axiosInstance.post("/create-project", payload);
      const raw = response.data;
      const data = raw?.data ?? raw?.project ?? raw;
      if (data || raw) {
        const created =
          data?.data ||
          data?.project ||
          data ||
          raw;

        const projectIdNum =
          extractCreatedProjectId(created) ??
          (() => {
            const raw = created?.Project_Id ?? created?.project_id ?? created?.id;
            const n = Number(raw);
            return Number.isFinite(n) && n > 0 ? n : null;
          })();

        const customerRaw =
          created?.client_id ??
          created?.client_id ??
          created?.CustomerId ??
          formData.clientId;
        const clientIdNum = Number(customerRaw);
        const projectName =
          created?.Project_Name ??
          created?.project_name ??
          created?.name ??
          formData.projectName;

        // Best-effort: create project subscription in FICO right after project creation.
        // (Attachment uploads are handled separately below.)
        let subscriptionResp = null;
        let subscriptionErrMsg = "";
        if (subscriptionForm.createOnProjectCreate && projectIdNum != null) {
          const start = subscriptionForm.startDate;
          let end = subscriptionForm.endDate;
          const planIdNum = Number(subscriptionForm.planId || "1");
          const status = subscriptionForm.status || "Active";

          // If user didn't provide an End date, default to +30 days (Monthly plan behavior).
          if (start && !end) {
            try {
              const dt = new Date(start + "T00:00:00");
              dt.setDate(dt.getDate() + 30);
              end = dt.toISOString().split("T")[0];
            } catch {
              // ignore - handled by missing date validation below
            }
          }

          if (!start || !end) {
            subscriptionErrMsg = "Subscription dates are missing (Start/End).";
          } else if (!planIdNum || Number.isNaN(planIdNum) || planIdNum <= 0) {
            subscriptionErrMsg = "Invalid Plan ID for subscription.";
          } else if (end < start) {
            subscriptionErrMsg = "Subscription End Date cannot be before Start Date.";
          } else {
            try {
              const subPayload = {
                Project_Id: Number(projectIdNum),
                Plan_Id: planIdNum,
                Start_Date: start,
                End_Date: end,
                Status: status,
                Created_By: createdBy
              };
              const amountNum = subscriptionForm.amount === "" || subscriptionForm.amount == null
                ? null
                : Number(String(subscriptionForm.amount).replace(/,/g, ""));
              if (amountNum != null && !Number.isNaN(amountNum)) {
                subPayload.Amount = amountNum;
              }
              const subRes = await axiosInstance.post("/fico/project-subscription", subPayload);
              const subRaw = subRes?.data;
              subscriptionResp = subRaw?.data ?? subRaw ?? null;
            } catch (subErr) {
              subscriptionErrMsg =
                subErr.response?.data?.message ||
                subErr.response?.data?.error ||
                subErr.message ||
                "Failed to create project subscription.";
            }
          }
        }

        if (subscriptionResp) setSubscriptionResult(subscriptionResp);
        if (subscriptionErrMsg) setSubscriptionError(subscriptionErrMsg);

        if (pendingFiles.length > 0) {
          if (projectIdNum == null) {
            alert(
              "Project was created, but the response did not include a project id, so attachments were not uploaded. Add them from View or Edit project."
            );
            setPendingFiles([]);
          } else {
            for (const file of pendingFiles) {
              try {
                await uploadProjectFile(projectIdNum, file);
              } catch (uploadErr) {
                console.error("Project file upload after create:", uploadErr);
                alert(
                  uploadErr.response?.data?.message ||
                    uploadErr.response?.data?.error ||
                    "Project created, but a file failed to upload. Add files from the project page."
                );
                setPendingFiles([]);
                if (onAddProject) await onAddProject();
                if (onBack) onBack();
                else navigate(-1);
                return;
              }
            }
            setPendingFiles([]);
          }
        }

        if (projectIdNum != null && Number.isFinite(clientIdNum) && clientIdNum > 0) {
          if (subscriptionResp) {
            alert("Project created successfully and subscription created.");
          } else if (subscriptionErrMsg) {
            alert("Project created successfully, but subscription failed: " + subscriptionErrMsg);
          } else {
            alert("Project created successfully");
          }
          if (onAddProject) await onAddProject();
          if (onBack) onBack();
          else navigate(-1);
          return;
        }

        // Fallback: if mapping fails, just close back to Projects.
        if (subscriptionResp) {
          alert("Project created successfully, and subscription created.");
        } else if (subscriptionErrMsg) {
          alert("Project created successfully, but subscription failed: " + subscriptionErrMsg);
        } else {
          alert("Project created successfully");
        }
        if (onAddProject) await onAddProject();
        if (onBack) onBack();
        else navigate(-1);
      } else {
        throw new Error("Create failed - no response data");
      }
    } catch (err) {
      console.error("Error creating project:", err);
      alert(err.response?.data?.message || err.response?.data?.error || "Failed to create project.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddSubproject = async (e) => {
    e.preventDefault();
    if (!createdProject?.projectId || !createdProject?.clientId) return;
    if (!subprojectForm.subProjectName || !subprojectForm.shortCode) {
      alert("Please fill in Subproject Name and Short Code.");
      return;
    }
    if (subprojectForm.startDate && subprojectForm.startDate < today) {
      alert("Start date cannot be in the past.");
      return;
    }
    if (subprojectForm.endDate && subprojectForm.endDate < today) {
      alert("End date cannot be in the past.");
      return;
    }
    if (subprojectForm.startDate && subprojectForm.endDate && subprojectForm.endDate < subprojectForm.startDate) {
      alert("End date cannot be before start date.");
      return;
    }
    if (
      subprojectForm.actualStartDate &&
      subprojectForm.actualEndDate &&
      subprojectForm.actualEndDate < subprojectForm.actualStartDate
    ) {
      alert("Actual end date cannot be before actual start date.");
      return;
    }
    let updatedBy = 1;
    try {
      const userStr = getAuthItem("user");
      if (userStr) {
        const user = JSON.parse(userStr);
        updatedBy = user.user_id || user.User_Id || user.id || 1;
      }
    } catch (err) {
      console.error("Error reading user:", err);
    }
    const payload = {
      client_id: Number(createdProject.clientId),
      Project_Id: Number(createdProject.projectId),
      Sub_Project_Name: subprojectForm.subProjectName.trim(),
      Short_Code: subprojectForm.shortCode.trim(),
      Description: subprojectForm.description?.trim() || null,
      Domain_Id: subprojectForm.domain ? Number(subprojectForm.domain) : null,
      Category_Id: subprojectForm.category ? Number(subprojectForm.category) : null,
      Visibility: subprojectForm.visibility || "Private",
      Start_Date: subprojectForm.startDate || null,
      End_Date: subprojectForm.endDate || null,
      Actual_Start_Date: subprojectForm.actualStartDate || null,
      Actual_End_Date: subprojectForm.actualEndDate || null,
      Updated_By: Number(updatedBy),
      User_Ids: subprojectForm.userIds.map((id) => Number(id)),
      Status_Id: subprojectForm.statusId ? parseInt(subprojectForm.statusId, 10) : 2
    };
    setSubprojectSubmitting(true);
    try {
      const response = await axiosInstance.post("/sub-projects", payload);
      const sp = response.data?.data || response.data;
      if (sp) {
        setSubprojects((prev) => [...prev, sp]);
        setSubprojectForm(INITIAL_SUBPROJECT_FORM);
        alert("Subproject added successfully");
      }
    } catch (err) {
      console.error("Error creating subproject:", err);
      alert(err.response?.data?.message || err.response?.data?.error || "Failed to create subproject.");
    } finally {
      setSubprojectSubmitting(false);
    }
  };

  const handleDoneWithSubprojects = () => {
    // Refresh projects list before leaving this page
    if (onAddProject) onAddProject();
    if (onBack) onBack();
    else navigate(-1);
  };

  const allUsersSelected = !loadingUsers && users.length > 0 && formData.userIds.length === users.length;
  const allSubUsersSelected = !loadingUsers && users.length > 0 && subprojectForm.userIds.length === users.length;

  const handleToggleSelectAllUsers = () => {
    setFormData((prev) => {
      if (allUsersSelected) {
        return { ...prev, userIds: [] };
      }
      return { ...prev, userIds: users.map((u) => u.id) };
    });
  };

  const handleToggleSelectAllSubUsers = () => {
    setSubprojectForm((prev) => {
      if (allSubUsersSelected) {
        return { ...prev, userIds: [] };
      }
      return { ...prev, userIds: users.map((u) => u.id) };
    });
  };

  const handleBackClick = () => {
    if (onBack) onBack();
    else navigate(-1);
  };

  if (createdProject) {
    return (
      <div className="project-form-container edittenant-container">
        <button type="button" className="viewlead-back-link" onClick={handleBackClick} style={{ marginBottom: 12 }}>
          <FaArrowLeft /> Back
        </button>
        <div className="project-subprojects-card">
          <h3 className="project-subprojects-title">Subprojects</h3>
          <p className="project-subprojects-subtitle">Project &quot;{createdProject.projectName}&quot; created. Add subprojects below (optional).</p>

          {canAddProjectFiles ? (
            <div className="viewlead-card issue-attachments-card project-attachments-card" style={{ marginBottom: 24 }}>
              <ProjectFileAttachments
                project={{
                  Project_Id: createdProject.projectId,
                  project_id: createdProject.projectId
                }}
                projectId={createdProject.projectId}
                canUpload={canAddProjectFiles}
                title="Project attachments"
              />
            </div>
          ) : null}

          <div className="issues-main-card">
          <form className="edittenant-form" onSubmit={handleAddSubproject}>
            <div className="form-section">
              <div className="form-grid">
                <div className="form-group">
                  <label>Subproject Name <span className="required">*</span></label>
                  <input type="text" name="subProjectName" value={subprojectForm.subProjectName} onChange={handleSubprojectInputChange} required />
                </div>
                <div className="form-group">
                  <label>Short Code <span className="required">*</span></label>
                  <input type="text" name="shortCode" value={subprojectForm.shortCode} onChange={handleSubprojectInputChange} required  maxLength={20} />
                </div>
                <div className="form-group">
                  <label>Domain</label>
                  <div className="domain-filter-dropdown" ref={subDomainDropdownRef} style={{ minWidth: "100%" }}>
                    <div className="search-filter-input search-filter-select domain-filter-trigger" style={{ width: "100%", height: 42, borderRadius: 5, border: "1px solid #D6E0EA", padding: "0 12px", fontSize: 14, background: "#fff", opacity: loadingDomains ? 0.6 : 1, cursor: loadingDomains ? "default" : "pointer" }} onClick={() => !loadingDomains && setSubDomainDropdownOpen((o) => !o)} role="button" tabIndex={loadingDomains ? -1 : 0} onKeyDown={(e) => { if (!loadingDomains && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); setSubDomainDropdownOpen((o) => !o); } }}>
                      <span>{loadingDomains ? "Loading domains..." : (subprojectForm.domain ? (domains.find((d) => String(d.id) === String(subprojectForm.domain))?.name || "Select Domain") : "Select Domain")}</span>
                      <FaChevronDown className={`domain-filter-chevron ${subDomainDropdownOpen ? "open" : ""}`} />
                    </div>
                    {subDomainDropdownOpen && !loadingDomains && (
                      <div className="domain-filter-panel">
                        <input type="text" className="domain-filter-search" placeholder="Search domains..." value={subDomainSearchQuery} onChange={(e) => setSubDomainSearchQuery(e.target.value)} onClick={(e) => e.stopPropagation()} autoFocus />
                        <div className="domain-filter-options">
                          <div className={`domain-filter-option ${!subprojectForm.domain ? "selected" : ""}`} onClick={() => { setSubprojectForm((p) => ({ ...p, domain: "" })); setSubDomainDropdownOpen(false); setSubDomainSearchQuery(""); }}>Select Domain</div>
                          {domains.filter((d) => !subDomainSearchQuery.trim() || String(d.name || "").toLowerCase().includes(subDomainSearchQuery.trim().toLowerCase())).map((d) => (
                            <div key={d.id} className={`domain-filter-option ${String(subprojectForm.domain) === String(d.id) ? "selected" : ""}`} onClick={() => { setSubprojectForm((p) => ({ ...p, domain: String(d.id) })); setSubDomainDropdownOpen(false); setSubDomainSearchQuery(""); }}>{d.name}</div>
                          ))}
                          {domains.filter((d) => !subDomainSearchQuery.trim() || String(d.name || "").toLowerCase().includes(subDomainSearchQuery.trim().toLowerCase())).length === 0 && <div className="domain-filter-no-results">No domains found</div>}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="form-group">
                  <label>Category</label>
                  <div className="domain-filter-dropdown" ref={subCategoryDropdownRef} style={{ minWidth: "100%" }}>
                    <div className="search-filter-input search-filter-select domain-filter-trigger" style={{ width: "100%", height: 42, borderRadius: 5, border: "1px solid #D6E0EA", padding: "0 12px", fontSize: 14, background: "#fff", opacity: loadingCategories ? 0.6 : 1, cursor: loadingCategories ? "default" : "pointer" }} onClick={() => !loadingCategories && setSubCategoryDropdownOpen((o) => !o)} role="button" tabIndex={loadingCategories ? -1 : 0} onKeyDown={(e) => { if (!loadingCategories && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); setSubCategoryDropdownOpen((o) => !o); } }}>
                      <span>{loadingCategories ? "Loading categories..." : (subprojectForm.category ? (categories.find((c) => String(c.id) === String(subprojectForm.category))?.name || "Select Category") : "Select Category")}</span>
                      <FaChevronDown className={`domain-filter-chevron ${subCategoryDropdownOpen ? "open" : ""}`} />
                    </div>
                    {subCategoryDropdownOpen && !loadingCategories && (
                      <div className="domain-filter-panel">
                        <input type="text" className="domain-filter-search" placeholder="Search categories..." value={subCategorySearchQuery} onChange={(e) => setSubCategorySearchQuery(e.target.value)} onClick={(e) => e.stopPropagation()} autoFocus />
                        <div className="domain-filter-options">
                          <div className={`domain-filter-option ${!subprojectForm.category ? "selected" : ""}`} onClick={() => { setSubprojectForm((p) => ({ ...p, category: "" })); setSubCategoryDropdownOpen(false); setSubCategorySearchQuery(""); }}>Select Category</div>
                          {categories.filter((c) => !subCategorySearchQuery.trim() || String(c.name || "").toLowerCase().includes(subCategorySearchQuery.trim().toLowerCase())).map((c) => (
                            <div key={c.id} className={`domain-filter-option ${String(subprojectForm.category) === String(c.id) ? "selected" : ""}`} onClick={() => { setSubprojectForm((p) => ({ ...p, category: String(c.id) })); setSubCategoryDropdownOpen(false); setSubCategorySearchQuery(""); }}>{c.name}</div>
                          ))}
                          {categories.filter((c) => !subCategorySearchQuery.trim() || String(c.name || "").toLowerCase().includes(subCategorySearchQuery.trim().toLowerCase())).length === 0 && <div className="domain-filter-no-results">No categories found</div>}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select
                    name="statusId"
                    value={subprojectForm.statusId}
                    onChange={handleSubprojectInputChange}
                    disabled={loadingStatuses}
                  >
                    <option value="">{loadingStatuses ? "Loading statuses..." : "Select Status"}</option>
                    {statuses.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Start Date</label>
                  <input type="date" name="startDate" value={subprojectForm.startDate} onChange={handleSubprojectInputChange} min={today} />
                </div>
                <div className="form-group">
                  <label>End Date</label>
                  <input type="date" name="endDate" value={subprojectForm.endDate} onChange={handleSubprojectInputChange} min={subprojectForm.startDate || today} />
                </div>
                <div className="form-group">
                  <label>Visibility</label>
                  <select name="visibility" value={subprojectForm.visibility} onChange={handleSubprojectInputChange}>
                    {VISIBILITY_OPTIONS.map((v) => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Actual Start Date</label>
                  <input type="date" name="actualStartDate" value={subprojectForm.actualStartDate} onChange={handleSubprojectInputChange} />
                </div>
                <div className="form-group">
                  <label>Actual End Date</label>
                  <input
                    type="date"
                    name="actualEndDate"
                    value={subprojectForm.actualEndDate}
                    onChange={handleSubprojectInputChange}
                    min={subprojectForm.actualStartDate || undefined}
                  />
                </div>
              </div>
              <div className="form-group form-group-multi-select" style={{ marginTop: 16 }}>
                <label>Team Members </label>
                <div ref={subTeamDropdownRef} style={{ marginTop: 8, maxWidth: 320, width: "100%", alignSelf: "flex-start" }}>
                  <div
                    className="permissions-select-trigger"
                    onClick={() => {
                      if (!loadingUsers) setShowSubTeamDropdown((prev) => !prev);
                    }}
                    style={{
                      opacity: loadingUsers ? 0.6 : 1,
                      cursor: loadingUsers ? "default" : "pointer"
                    }}
                  >
                    <span className="permissions-select-text">
                      {loadingUsers
                        ? "Loading team members..."
                        : subprojectForm.userIds.length === 0
                          ? "Select team members"
                          : `${subprojectForm.userIds.length} member${subprojectForm.userIds.length > 1 ? "s" : ""} selected`}
                    </span>
                    <FaChevronDown
                      className={`permissions-select-chevron ${showSubTeamDropdown ? "open" : ""}`}
                    />
                  </div>
                  {showSubTeamDropdown && !loadingUsers && (
                    <div
                      className="permissions-select-panel"
                      style={{ maxHeight: 220, overflowY: "auto" }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input type="text" className="domain-filter-search" placeholder="Search team members..." value={subTeamMemberSearchQuery} onChange={(e) => setSubTeamMemberSearchQuery(e.target.value)} onClick={(e) => e.stopPropagation()} style={{ margin: "8px", width: "calc(100% - 16px)", boxSizing: "border-box" }} />
                      <label className="permissions-select-item">
                        <input
                          type="checkbox"
                          checked={allSubUsersSelected}
                          onChange={handleToggleSelectAllSubUsers}
                        />
                        <span className="permissions-select-item-label">Select all</span>
                      </label>
                      {users.filter((u) => !subTeamMemberSearchQuery.trim() || String(u.name || "").toLowerCase().includes(subTeamMemberSearchQuery.trim().toLowerCase())).map((u) => (
                        <label
                          key={u.id}
                          className="permissions-select-item"
                        >
                          <input
                            type="checkbox"
                            checked={subprojectForm.userIds.includes(u.id)}
                            onChange={() => handleSubprojectUserToggle(u.id)}
                          />
                          <span className="permissions-select-item-label">{u.name}</span>
                        </label>
                      ))}
                      {users.filter((u) => !subTeamMemberSearchQuery.trim() || String(u.name || "").toLowerCase().includes(subTeamMemberSearchQuery.trim().toLowerCase())).length === 0 && <div className="domain-filter-no-results" style={{ padding: "12px" }}>No team members found</div>}
                    </div>
                  )}
                </div>
              </div>
              <div className="form-group" style={{ marginTop: 16 }}>
                <label>Description</label>
                <textarea name="description" value={subprojectForm.description} onChange={handleSubprojectInputChange} rows="4" />
              </div>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn-update" disabled={subprojectSubmitting}>
                {subprojectSubmitting ? "Adding..." : "Add Subproject"}
              </button>
              <button type="button" className="btn-cancel" onClick={handleDoneWithSubprojects} style={{ marginLeft: 10 }}>
                Done
              </button>
            </div>
          </form>
          </div>

          {subprojects.length > 0 && (
            <div className="project-subprojects-list">
              <h4 className="project-subprojects-list-title">Added Subprojects</h4>
              <ul>
                {subprojects.map((sp) => (
                  <li key={sp.Sub_Project_Id || sp.sub_project_id}>
                    <strong>{sp.Sub_Project_Name || sp.sub_project_name}</strong>
                    <span className="project-subprojects-list-code">({sp.Short_Code || sp.short_code})</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="project-form-container edittenant-container">
      <button type="button" className="viewlead-back-link" onClick={handleBackClick} style={{ marginBottom: 12 }}>
        <FaArrowLeft /> Back
      </button>
      <div className="issues-main-card">
      <form className="edittenant-form" onSubmit={handleSubmit}>
        <div className="form-section">
            <div className="form-grid">
            <div className="form-group">
              <label>Project Name <span className="required">*</span></label>
              <input type="text" name="projectName" value={formData.projectName} onChange={handleInputChange} required  />
            </div>
            <div className="form-group">
              <label>Short Code <span className="required">*</span></label>
              <input type="text" name="shortCode" value={formData.shortCode} onChange={handleInputChange} required  maxLength={20} />
            </div>
            <div className="form-group">
              <label>Tenant <span className="required">*</span></label>
              <div className="domain-filter-dropdown" ref={customerDropdownRef} style={{ minWidth: "100%" }}>
                <div className="search-filter-input search-filter-select domain-filter-trigger" style={{ width: "100%", height: 42, borderRadius: 5, border: "1px solid #D6E0EA", padding: "0 12px", fontSize: 14, background: "#fff", opacity: loadingCustomers ? 0.6 : 1, cursor: loadingCustomers ? "default" : "pointer" }} onClick={() => !loadingCustomers && setCustomerDropdownOpen((o) => !o)} role="button" tabIndex={loadingCustomers ? -1 : 0} onKeyDown={(e) => { if (!loadingCustomers && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); setCustomerDropdownOpen((o) => !o); } }}>
                  <span>{loadingCustomers ? "Loading tenants..." : (formData.clientId ? (() => { const sel = customers.find((c) => String(c.id) === String(formData.clientId)); return sel ? getCustomerSelectLabel(sel) : "Select Tenant"; })() : "Select Tenant")}</span>
                  <FaChevronDown className={`domain-filter-chevron ${customerDropdownOpen ? "open" : ""}`} />
                </div>
                {customerDropdownOpen && !loadingCustomers && (
                  <div className="domain-filter-panel">
                    <input type="text" className="domain-filter-search" placeholder="Search tenants..." value={customerSearchQuery} onChange={(e) => setCustomerSearchQuery(e.target.value)} onClick={(e) => e.stopPropagation()} autoFocus />
                    <div className="domain-filter-options">
                      <div className={`domain-filter-option ${!formData.clientId ? "selected" : ""}`} onClick={() => { setFormData((p) => ({ ...p, clientId: "" })); setCustomerDropdownOpen(false); setCustomerSearchQuery(""); }}>Select Tenant</div>
                      {customers.filter((c) => !customerSearchQuery.trim() || getCustomerSelectSearchText(c).includes(customerSearchQuery.trim().toLowerCase())).map((c) => (
                        <div key={c.id} className={`domain-filter-option ${String(formData.clientId) === String(c.id) ? "selected" : ""}`} onClick={() => { setFormData((p) => ({ ...p, clientId: String(c.id) })); setCustomerDropdownOpen(false); setCustomerSearchQuery(""); }}>{getCustomerSelectLabel(c)}</div>
                      ))}
                      {customers.filter((c) => !customerSearchQuery.trim() || getCustomerSelectSearchText(c).includes(customerSearchQuery.trim().toLowerCase())).length === 0 && <div className="domain-filter-no-results">No tenants found</div>}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="form-group">
              <label>Domain</label>
              <div className="domain-filter-dropdown" ref={domainDropdownRef} style={{ minWidth: "100%" }}>
                <div className="search-filter-input search-filter-select domain-filter-trigger" style={{ width: "100%", height: 42, borderRadius: 5, border: "1px solid #D6E0EA", padding: "0 12px", fontSize: 14, background: "#fff", opacity: loadingDomains ? 0.6 : 1, cursor: loadingDomains ? "default" : "pointer" }} onClick={() => !loadingDomains && setDomainDropdownOpen((o) => !o)} role="button" tabIndex={loadingDomains ? -1 : 0} onKeyDown={(e) => { if (!loadingDomains && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); setDomainDropdownOpen((o) => !o); } }}>
                  <span>{loadingDomains ? "Loading domains..." : (formData.domain ? (domains.find((d) => String(d.id) === String(formData.domain))?.name || "Select Domain") : "Select Domain")}</span>
                  <FaChevronDown className={`domain-filter-chevron ${domainDropdownOpen ? "open" : ""}`} />
                </div>
                {domainDropdownOpen && !loadingDomains && (
                  <div className="domain-filter-panel">
                    <input type="text" className="domain-filter-search" placeholder="Search domains..." value={domainSearchQuery} onChange={(e) => setDomainSearchQuery(e.target.value)} onClick={(e) => e.stopPropagation()} autoFocus />
                    <div className="domain-filter-options">
                      <div className={`domain-filter-option ${!formData.domain ? "selected" : ""}`} onClick={() => { setFormData((p) => ({ ...p, domain: "" })); setDomainDropdownOpen(false); setDomainSearchQuery(""); }}>Select Domain</div>
                      {domains.filter((d) => !domainSearchQuery.trim() || String(d.name || "").toLowerCase().includes(domainSearchQuery.trim().toLowerCase())).map((d) => (
                        <div key={d.id} className={`domain-filter-option ${String(formData.domain) === String(d.id) ? "selected" : ""}`} onClick={() => { setFormData((p) => ({ ...p, domain: String(d.id) })); setDomainDropdownOpen(false); setDomainSearchQuery(""); }}>{d.name}</div>
                      ))}
                      {domains.filter((d) => !domainSearchQuery.trim() || String(d.name || "").toLowerCase().includes(domainSearchQuery.trim().toLowerCase())).length === 0 && <div className="domain-filter-no-results">No domains found</div>}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="form-group">
              <label>Category</label>
              <div className="domain-filter-dropdown" ref={categoryDropdownRef} style={{ minWidth: "100%" }}>
                <div className="search-filter-input search-filter-select domain-filter-trigger" style={{ width: "100%", height: 42, borderRadius: 5, border: "1px solid #D6E0EA", padding: "0 12px", fontSize: 14, background: "#fff", opacity: loadingCategories ? 0.6 : 1, cursor: loadingCategories ? "default" : "pointer" }} onClick={() => !loadingCategories && setCategoryDropdownOpen((o) => !o)} role="button" tabIndex={loadingCategories ? -1 : 0} onKeyDown={(e) => { if (!loadingCategories && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); setCategoryDropdownOpen((o) => !o); } }}>
                  <span>{loadingCategories ? "Loading categories..." : (formData.category ? (categories.find((c) => String(c.id) === String(formData.category))?.name || "Select Category") : "Select Category")}</span>
                  <FaChevronDown className={`domain-filter-chevron ${categoryDropdownOpen ? "open" : ""}`} />
                </div>
                {categoryDropdownOpen && !loadingCategories && (
                  <div className="domain-filter-panel">
                    <input type="text" className="domain-filter-search" placeholder="Search categories..." value={categorySearchQuery} onChange={(e) => setCategorySearchQuery(e.target.value)} onClick={(e) => e.stopPropagation()} autoFocus />
                    <div className="domain-filter-options">
                      <div className={`domain-filter-option ${!formData.category ? "selected" : ""}`} onClick={() => { setFormData((p) => ({ ...p, category: "" })); setCategoryDropdownOpen(false); setCategorySearchQuery(""); }}>Select Category</div>
                      {categories.filter((c) => !categorySearchQuery.trim() || String(c.name || "").toLowerCase().includes(categorySearchQuery.trim().toLowerCase())).map((c) => (
                        <div key={c.id} className={`domain-filter-option ${String(formData.category) === String(c.id) ? "selected" : ""}`} onClick={() => { setFormData((p) => ({ ...p, category: String(c.id) })); setCategoryDropdownOpen(false); setCategorySearchQuery(""); }}>{c.name}</div>
                      ))}
                      {categories.filter((c) => !categorySearchQuery.trim() || String(c.name || "").toLowerCase().includes(categorySearchQuery.trim().toLowerCase())).length === 0 && <div className="domain-filter-no-results">No categories found</div>}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="form-group">
              <label>Visibility</label>
              <select name="visibility" value={formData.visibility} onChange={handleInputChange}>
                {VISIBILITY_OPTIONS.map((v) => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Status</label>
              <select
                name="statusId"
                value={formData.statusId}
                onChange={handleInputChange}
                disabled={loadingStatuses}
              >
                <option value="">{loadingStatuses ? "Loading statuses..." : "Select Status"}</option>
                {statuses.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>
                Total cost <span className="required">*</span>
              </label>
              <input
                type="number"
                name="totalCost"
                min="0"
                step="0.01"
                value={formData.totalCost}
                onChange={handleInputChange}

                required
              />
            </div>
            <div className="form-group">
              <label>
                Currency <span className="required">*</span>
              </label>
              <select
                name="currencyId"
                value={formData.currencyId}
                onChange={handleInputChange}
                disabled={loadingCurrencies}
                required
              >
                <option value="">{loadingCurrencies ? "Loading currencies..." : "Select currency"}</option>
                {currencies.map((c) => (
                  <option key={c.id} value={String(c.id)}>
                    {(c.symbol || c.code || c.name || "").trim()}
                  </option>
                ))}
              </select>
              {currencyFetchError && !loadingCurrencies ? (
                <p style={{ margin: "6px 0 0", fontSize: 12, color: "#b42318" }}>{currencyFetchError}</p>
              ) : null}
            </div>
            <div className="form-group">
              <label>Start date</label>
              <input type="date" name="startDate" value={formData.startDate} onChange={handleInputChange} min={today} />
            </div>
            <div className="form-group">
              <label>End date</label>
              <input type="date" name="endDate" value={formData.endDate} onChange={handleInputChange} min={formData.startDate || today} />
            </div>

            <div className="form-group">
              <label>Actual start date</label>
              <input
                type="date"
                name="actualStartDate"
                value={formData.actualStartDate}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>Actual end date</label>
              <input
                type="date"
                name="actualEndDate"
                value={formData.actualEndDate}
                onChange={handleInputChange}
                min={formData.actualStartDate || undefined}
              />
            </div>
            <div className="form-group project-priority-seq-group">
              <label>Priority sequence</label>
              <input
                type="text"
                name="prioritySeq"
                value={formData.prioritySeq}
                onChange={handleInputChange}
               
              />
            </div>
            <div className="form-group form-group-multi-select project-team-members-inline-group">
              <label>Team Members (Users)</label>
              <div ref={teamDropdownRef} style={{ width: "100%", maxWidth: "100%" }}>
                <div
                  className="permissions-select-trigger"
                  onClick={() => {
                    if (!loadingUsers) setShowTeamDropdown((prev) => !prev);
                  }}
                  style={{
                    opacity: loadingUsers ? 0.6 : 1,
                    cursor: loadingUsers ? "default" : "pointer"
                  }}
                >
                  <span className="permissions-select-text">
                    {loadingUsers
                      ? "Loading team members..."
                      : formData.userIds.length === 0
                        ? "Select team members"
                        : `${formData.userIds.length} member${formData.userIds.length > 1 ? "s" : ""} selected`}
                  </span>
                  <FaChevronDown
                    className={`permissions-select-chevron ${showTeamDropdown ? "open" : ""}`}
                  />
                </div>
                {showTeamDropdown && !loadingUsers && (
                  <div
                    className="permissions-select-panel"
                    style={{ maxHeight: 220, overflowY: "auto" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input type="text" className="domain-filter-search" placeholder="Search team members..." value={teamMemberSearchQuery} onChange={(e) => setTeamMemberSearchQuery(e.target.value)} onClick={(e) => e.stopPropagation()} style={{ margin: "8px", width: "calc(100% - 16px)", boxSizing: "border-box" }} />
                    <label className="permissions-select-item">
                      <input
                        type="checkbox"
                        checked={allUsersSelected}
                        onChange={handleToggleSelectAllUsers}
                      />
                      <span className="permissions-select-item-label">Select all</span>
                    </label>
                    {users.filter((u) => !teamMemberSearchQuery.trim() || String(u.name || "").toLowerCase().includes(teamMemberSearchQuery.trim().toLowerCase())).map((u) => (
                      <label
                        key={u.id}
                        className="permissions-select-item"
                      >
                        <input
                          type="checkbox"
                          checked={formData.userIds.includes(u.id)}
                          onChange={() => handleUserToggle(u.id)}
                        />
                        <span className="permissions-select-item-label">{u.name}</span>
                      </label>
                    ))}
                    {users.filter((u) => !teamMemberSearchQuery.trim() || String(u.name || "").toLowerCase().includes(teamMemberSearchQuery.trim().toLowerCase())).length === 0 && <div className="domain-filter-no-results" style={{ padding: "12px" }}>No team members found</div>}
                  </div>
                )}
              </div>
            </div>
            </div>
            <div className="form-group" style={{ marginTop: 16 }}>
              <label>Description</label>
              <textarea name="description" value={formData.description} onChange={handleInputChange} rows="4" />
            </div>
            <div className="form-group" style={{ marginTop: 16 }}>
              <label>Notes</label>
              <textarea name="notes" value={formData.notes} onChange={handleInputChange} rows="4" />
            </div>

            <div style={{ gridColumn: "1 / -1", marginTop: 16 }}>
              <h4 style={{ margin: "0 0 10px 0", fontSize: 16, fontWeight: 600, color: "#172B4D" }}>
                Project Subscription
              </h4>

              <label className="checkbox-label" style={{ margin: 0, display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="checkbox"
                  name="createOnProjectCreate"
                  checked={subscriptionForm.createOnProjectCreate}
                  onChange={handleSubscriptionChange}
                />
                <span>Create subscription</span>
              </label>

              <div className="project-subscription-form-grid" style={{ marginTop: 12 }}>
                <div className="form-group">
                  <label>Plan Name</label>
                  <select
                    name="planId"
                    value={subscriptionForm.planId}
                    onChange={handleSubscriptionChange}
                    disabled={!subscriptionForm.createOnProjectCreate || loadingPlans}
                  >
                    <option value="">{loadingPlans ? "Loading plans..." : "Select plan"}</option>
                    {plans.map((pl) => (
                      <option key={pl.id} value={String(pl.id)}>
                        {pl.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Subscription Start Date</label>
                  <input
                    type="date"
                    name="startDate"
                    value={subscriptionForm.startDate}
                    onChange={handleSubscriptionChange}
                    min={today}
                    disabled={!subscriptionForm.createOnProjectCreate}
                  />
                </div>
                <div className="form-group">
                  <label>Subscription End Date</label>
                  <input
                    type="date"
                    name="endDate"
                    value={subscriptionForm.endDate}
                    onChange={handleSubscriptionChange}
                    min={subscriptionForm.startDate || today}
                    disabled={!subscriptionForm.createOnProjectCreate}
                  />
                </div>

                <div className="form-group">
                  <label>Status</label>
                  <select
                    name="status"
                    value={subscriptionForm.status}
                    onChange={handleSubscriptionChange}
                    disabled={!subscriptionForm.createOnProjectCreate}
                  >
                    <option value="Active">Active</option>
                    <option value="Expired">Expired</option>
                    <option value="Cancelled">Cancelled</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Amount</label>
                  <input
                    type="text"
                    name="amount"
                    inputMode="decimal"
                    pattern="^[0-9]*[.,]?[0-9]*$"
                    value={subscriptionForm.amount}
                    onChange={handleSubscriptionChange}
                    disabled={!subscriptionForm.createOnProjectCreate}
                
                  />
                </div>

                
              </div>

              {subscriptionError ? (
                <div className="project-subscription-error" role="alert" style={{ marginTop: 12 }}>
                  {subscriptionError}
                </div>
              ) : null}

              {subscriptionResult ? (
                <div className="project-subscription-success" style={{ marginTop: 12 }}>
                  Subscription created successfully. Amount: {subscriptionResult.Amount ?? "—"} | End:{" "}
                  {subscriptionResult.End_Date ?? "—"}
                </div>
              ) : null}
            </div>

            <div
              className="add-project-attachments-team-row"
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 24,
                alignItems: "flex-start",
                marginTop: 16,
                width: "100%"
              }}
            >
              {canAddProjectFiles ? (
                <div className="form-group issues-description-full" style={{ flex: "1 1 300px", minWidth: 0, marginTop: 0 }}>
                  <label>Attachments </label>
                  <input
                    type="file"
                    className="issue-files-native-input"
                    disabled={submitting}
                    multiple
                    onChange={(e) => {
                      const next = Array.from(e.target.files || []);
                      setPendingFiles((prev) => [...prev, ...next]);
                      e.target.value = "";
                    }}
                  />
                  {pendingFiles.length > 0 ? (
                    <ul className="issue-files-pending-list">
                      {pendingFiles.map((f, idx) => (
                        <li key={`${f.name}-${idx}`} className="issue-files-pending-item">
                          <span className="issue-files-pending-name">{f.name}</span>
                          <button
                            type="button"
                            className="issue-files-pending-remove"
                            onClick={() => setPendingFiles((prev) => prev.filter((_, i) => i !== idx))}
                            disabled={submitting}
                            title="Remove"
                          >
                            <FaTimes />
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              ) : null}
            </div>
        </div>
        <div
          className="form-actions edittenant-actions"
          style={{
            display: "inline-flex",
            flexDirection: "row",
            flexWrap: "nowrap",
            alignItems: "center",
            gap: 10,
            borderTop: "none",
            paddingTop: 0
          }}
        >
          <button type="submit" className="btn-subproject-primary" disabled={submitting}>
            {submitting ? "Creating..." : "Create Project"}
          </button>
        </div>
      </form>
      </div>
    </div>
  );
}

export default AddProject;
